// src/services/api.js
import axios from 'axios';
import API_URLS from '../config/api';

// Configure base URL based on environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for CSRF and session cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add CSRF token
api.interceptors.request.use(
  (config) => {
    const csrfToken = getCookie('csrftoken');
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      // Forbidden - insufficient permissions
      console.error('Access denied: Insufficient permissions');
    }
    return Promise.reject(error);
  }
);

// Helper function to get CSRF cookie
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// ==================== AUTHENTICATION ENDPOINTS ====================

export const authAPI = {
  // Get CSRF token
  getCSRFToken: async () => {
    try {
      const response = await axios.get(API_URLS.CSRF_TOKEN, { withCredentials: true });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      return { success: false, error: error.response?.data || error.message };
    }
  },

  // Admin signup
  adminSignup: async (userData) => {
    try {
      const response = await axios.post(API_URLS.ADMIN_SIGNUP, userData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error during admin signup:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Signup failed',
        error: error.response?.data || error.message,
      };
    }
  },

  // Officer signup
  officerSignup: async (userData) => {
    try {
      const response = await axios.post(API_URLS.OFFICER_SIGNUP, userData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error during officer signup:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Officer signup failed',
        error: error.response?.data || error.message,
      };
    }
  },

  // Login
  login: async (username, password) => {
    try {
      const response = await axios.post(
        API_URLS.LOGIN,
        { username, password },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
          },
        }
      );
      return {
        success: true,
        data: response.data,
        user: response.data.user,
      };
    } catch (error) {
      console.error('Error during login:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
        error: error.response?.data || error.message,
      };
    }
  },

  // Logout
  logout: async () => {
    try {
      const response = await axios.post(API_URLS.LOGOUT, {}, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error during logout:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Logout failed',
        error: error.response?.data || error.message,
      };
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await axios.get(API_URLS.CURRENT_USER, { 
        withCredentials: true 
      });
      return {
        success: true,
        data: response.data,
        isAuthenticated: response.data.isAuthenticated,
        user: response.data.user,
      };
    } catch (error) {
      console.error('Error fetching current user:', error);
      return {
        success: false,
        isAuthenticated: false,
        error: error.response?.data || error.message,
      };
    }
  },
};

// ==================== USER MANAGEMENT ENDPOINTS (ADMIN ONLY) ====================

export const userAPI = {
  // List all users
  listUsers: async () => {
    try {
      const response = await axios.get(API_URLS.USER_LIST, { 
        withCredentials: true 
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching users:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch users',
        error: error.response?.data || error.message,
      };
    }
  },

  // Deactivate or activate user
  toggleUserStatus: async (userId, isActive) => {
    try {
      const response = await axios.post(
        API_URLS.USER_DEACTIVATE(userId),
        { is_active: isActive },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error toggling user status:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update user status',
        error: error.response?.data || error.message,
      };
    }
  },
};

// ==================== DASHBOARD ENDPOINTS ====================

export const dashboardAPI = {
  // Get dashboard statistics
  getStats: async () => {
    try {
      const response = await axios.get(API_URLS.DASHBOARD_STATS, { 
        withCredentials: true 
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch dashboard stats',
        error: error.response?.data || error.message,
      };
    }
  },

  // Get strategy performance
  getPerformance: async (timeRange = 'week') => {
    try {
      const response = await axios.get(API_URLS.DASHBOARD_PERFORMANCE, {
        params: { time_range: timeRange },
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching performance data:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch performance',
        error: error.response?.data || error.message,
      };
    }
  },
};

// ==================== STRATEGY ENDPOINTS (NEW SYSTEM) ====================

export const strategyAPI = {
  // Setup/create strategy
  setupStrategy: async (strategyData) => {
    try {
      const response = await axios.post(API_URLS.STRATEGY_SETUP, strategyData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error setting up strategy:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to setup strategy',
        error: error.response?.data || error.message,
      };
    }
  },

  // List all strategies
  listStrategies: async (filters = {}) => {
    try {
      const response = await axios.get(API_URLS.STRATEGY_LIST, {
        params: filters,
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching strategies:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch strategies',
        error: error.response?.data || error.message,
      };
    }
  },

  // Get strategy state
  getStrategy: async (strategyId) => {
    try {
      const response = await axios.get(API_URLS.STRATEGY_STATE(strategyId), {
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching strategy:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch strategy',
        error: error.response?.data || error.message,
      };
    }
  },

  // Reset strategy
  resetStrategy: async (strategyId) => {
    try {
      const response = await axios.post(
        API_URLS.STRATEGY_RESET,
        { strategy_id: strategyId },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error resetting strategy:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to reset strategy',
        error: error.response?.data || error.message,
      };
    }
  },

  // Delete strategy
  deleteStrategy: async (strategyId) => {
    try {
      const response = await axios.delete(API_URLS.STRATEGY_DELETE(strategyId), {
        withCredentials: true,
        headers: {
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error deleting strategy:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to delete strategy',
        error: error.response?.data || error.message,
      };
    }
  },

  // Update strategy (wrapper for setupStrategy)
  updateStrategy: async (strategyId, strategyData) => {
    return await strategyAPI.setupStrategy(strategyData);
  },

  // Create strategy (wrapper for setupStrategy)
  createStrategy: async (strategyData) => {
    return await strategyAPI.setupStrategy(strategyData);
  },


  // Get detailed strategy information
  getStrategyDetail: async (strategyId) => {
    try {
      const response = await axios.get(API_URLS.STRATEGY_DETAIL(strategyId), {
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching strategy detail:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch strategy detail',
        error: error.response?.data || error.message,
      };
    }
  },
  
};

// ==================== SIGNAL ENDPOINTS ====================

export const signalAPI = {
  // Send signal alert
  sendSignalAlert: async (signalData) => {
    try {
      const response = await axios.post(API_URLS.SIGNAL_ALERT, signalData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error sending signal alert:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to send signal',
        error: error.response?.data || error.message,
      };
    }
  },

  // Get detected signals history
  getDetectedSignals: async (strategyId, params = {}) => {
    try {
      const response = await axios.get(API_URLS.SIGNALS_HISTORY(strategyId), {
        params: params,
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching detected signals:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch signals',
        error: error.response?.data || error.message,
      };
    }
  },

  // Get signal events log
  getSignalEvents: async (strategyId, params = {}) => {
    try {
      const response = await axios.get(API_URLS.SIGNAL_EVENTS(strategyId), {
        params: params,
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching signal events:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch events',
        error: error.response?.data || error.message,
      };
    }
  },

  // List signals (backward compatibility)
  listSignals: async (filters = {}) => {
    console.warn('listSignals is deprecated, use getDetectedSignals instead');
    return { success: false, message: 'Use getDetectedSignals instead' };
  },

  // Get signal by ID (backward compatibility)
  getSignal: async (signalId) => {
    console.warn('getSignal by ID is deprecated');
    return { success: false, message: 'Use getDetectedSignals with filters instead' };
  },
};

// ==================== ALERT ENDPOINTS (OLD SYSTEM - DEPRECATED) ====================

export const alertAPI = {
  // NEW: Get historical alerts with pagination and filters
  getHistoricalAlerts: async (params = {}) => {
    try {
      const response = await axios.get(API_URLS.ALERTS_HISTORY, {
        params: {
          page: params.page || 1,
          limit: params.limit || 50,
          strategy_id: params.strategy_id,
          start_date: params.start_date,
          end_date: params.end_date,
          condition_type: params.condition_type,
        },
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching historical alerts:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch alerts',
        error: error.response?.data || error.message,
      };
    }
  },

  // NEW: Get alert statistics
  getAlertStats: async (timeRange = '7d') => {
    try {
      const response = await axios.get(API_URLS.ALERT_STATS, {
        params: { time_range: timeRange },
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching alert stats:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch stats',
        error: error.response?.data || error.message,
      };
    }
  },

  // List all alerts (OLD SYSTEM - keep for compatibility)
  listAlerts: async (filters = {}) => {
    console.warn('Old alert system - consider using getHistoricalAlerts instead');
    try {
      const response = await axios.get(API_URLS.ALERTS_LIST, {
        params: filters,
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching alerts:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch alerts',
        error: error.response?.data || error.message,
      };
    }
  },

  // Get alert by ID
  getAlert: async (alertId) => {
    try {
      const response = await axios.get(API_URLS.ALERT_DETAIL(alertId), {
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching alert:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch alert',
        error: error.response?.data || error.message,
      };
    }
  },

  // Create alert (admin only)
  createAlert: async (alertData) => {
    try {
      const response = await axios.post(API_URLS.ALERT_CREATE, alertData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating alert:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create alert',
        error: error.response?.data || error.message,
      };
    }
  },

  // Update alert (admin only)
  updateAlert: async (alertId, alertData) => {
    try {
      const response = await axios.put(API_URLS.ALERT_DETAIL(alertId), alertData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating alert:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update alert',
        error: error.response?.data || error.message,
      };
    }
  },

  // Delete alert (admin only)
  deleteAlert: async (alertId) => {
    try {
      const response = await axios.delete(API_URLS.ALERT_DETAIL(alertId), {
        withCredentials: true,
        headers: {
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error deleting alert:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete alert',
        error: error.response?.data || error.message,
      };
    }
  },
};


// ==================== MONITORING ENDPOINTS ====================

export const monitoringAPI = {
  // Get condition history
  getConditionHistory: async (params = {}) => {
    try {
      const response = await axios.get(API_URLS.CONDITION_HISTORY, {
        params: {
          strategy_id: params.strategy_id,
          hours: params.hours || 24,
        },
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching condition history:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch condition history',
        error: error.response?.data || error.message,
      };
    }
  },

  // Get strategy activity
  getStrategyActivity: async (hours = 24) => {
    try {
      const response = await axios.get(API_URLS.STRATEGY_ACTIVITY, {
        params: { hours },
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching strategy activity:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch strategy activity',
        error: error.response?.data || error.message,
      };
    }
  },
};

// ==================== REPORT ENDPOINTS ====================

export const reportAPI = {
  // Get performance report
  getPerformanceReport: async (timeRange = 'week') => {
    try {
      // Using dashboard performance as fallback
      const response = await axios.get(API_URLS.DASHBOARD_PERFORMANCE, {
        params: { time_range: timeRange },
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching performance report:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch report',
        error: error.response?.data || error.message,
      };
    }
  },

  // Get strategy performance
  getStrategyPerformance: async (strategyId, timeRange = 'week') => {
    try {
      const response = await axios.get(API_URLS.STRATEGY_STATE(strategyId), {
        params: { time_range: timeRange },
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching strategy performance:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch strategy performance',
        error: error.response?.data || error.message,
      };
    }
  },

  // Export report
  exportReport: async (reportType, format = 'csv') => {
    console.warn('Export report not implemented yet');
    return {
      success: false,
      message: 'Export functionality not implemented',
    };
  },




  // Get detailed alert information
  getAlertDetail: async (alertId) => {
    try {
      const response = await axios.get(API_URLS.ALERT_DETAIL(alertId), {
        withCredentials: true,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching alert detail:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to fetch alert detail',
        error: error.response?.data || error.message,
      };
    }
  },

};

// ==================== AGENT CHAT ====================

export const agentAPI = {
  chat: async (message) => {
    try {
      const response = await api.post(API_URLS.AGENT_CHAT, { message });
      return { success: true, reply: response.data.reply };
    } catch (error) {
      console.error('Error calling agent chat:', error);
      const data = error.response?.data || {};
      return {
        success: false,
        reply: null,
        error: data.error || error.message,
        detail: data.detail || '',
      };
    }
  },
};

// Export the axios instance for direct use if needed
export default api;
